import { BrowserRouter, Link, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import HomeScreen from './screens/HomeScreen';
import Navbar from 'react-bootstrap/Navbar';
import Badge from 'react-bootstrap/Badge';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Container from 'react-bootstrap/Container';
import { useContext, useState, useEffect, useRef } from 'react';
import { Store } from './Store';
import CartScreen from './screens/CartScreen';
import SigninScreen from './screens/SigninScreen';
import ShippingAddressScreen from './screens/ShippingAddressScreen';
import SignupScreen from './screens/SignupScreen';
import PaymentMethodScreen from './screens/PaymentMethodScreen';
import PlaceOrderScreen from './screens/PlaceOrderScreen';
import OrderScreen from './screens/OrderScreen';
import OrderHistoryScreen from './screens/OrderHistoryScreen';
import ProfileScreen from './screens/ProfileScreen';
import ProtectedRoute from './components/general/ProtectedRoute';
import AdminDashboardScreen from './screens/AdminDashboardScreen';
import LayoutListScreen from './screens/LayoutListScreen';
import LayoutEditScreen from './screens/LayoutEditScreen';
import AdminRoute from './components/general/AdminRoute';
import ProductListScreen from './screens/ProductListScreen';
import ProductEditScreen from './screens/ProductEditScreen';
import OrderListScreen from './screens/OrderListScreen';
import UserListScreen from './screens/UserListScreen';
import UserEditScreen from './screens/UserEditScreen';
import DesignListScreen from './screens/DesignListScreen';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import ImprintScreen from './screens/ImprintScreen';
import CreditsAndLicensesScreen from './screens/CreditsAndLicensesScreen';
import ScrollToTop from './components/general/ScrollToTop';
import FAQScreen from './screens/FAQScreen';
import GeneralTermsAndConditionsScreen from './screens/GeneralTermsAndConditionsScreen';
import RightOfWithdrawalScreen from './screens/RightOfWithdrawalScreen';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen';
import ContactScreen from './screens/ContactScreen';
import CustomizerScreen from './screens/CustomizerScreen';
import StockScreen from './screens/StockScreen';
import KeycapEditScreen from './screens/KeycapEditScreen';
import CardboardBoxEditScreen from './screens/CardboardBoxEditScreen';
import FoamEditScreen from './screens/FoamEditScreen';
import TransferFilmEditScreen from './screens/TransferFilmEditScreen';
import InkEditScreen from './screens/InkEditScreen';
import KeycapPullerEditScreen from './screens/KeycapPullerEditScreen';
import ShippingBoxEditScreen from './screens/ShippingBoxEditScreen';
import MyDesignsScreen from './screens/MyDesignsScreen';
import CommunityDesignsScreen from './screens/CommunityDesignsScreen';
import ColorsScreen from './screens/ColorsScreen';
import EmailVerificationScreen from './screens/EmailVerificationScreen';
import LegendsListScreen from './screens/LegendsListScreen';
import LegendsEditScreen from './screens/LegendsEditScreen';
import FontListScreen from './screens/FontListScreen';
import FontEditScreen from './screens/FontEditScreen';
import MoldsListScreen from './screens/MoldListScreen';
import MoldEditScreen from './screens/MoldEditScreen';
import ProcessOrderScreen from './screens/ProcessOrderScreen';
import EmailVerificationPromtScreen from './screens/EmailVerificationPromtScreen';
import ZoneListScreen from './screens/ZoneListScreen';
import ZoneEditScreen from './screens/ZoneEditScreen';
import ShippingCostsScreen from './screens/ShippingCostsScreen';
import React, { Fragment } from 'react';
import { LoadScript } from '@react-google-maps/api';
import { useSamsungBrowserBanner } from './utils/useSamsungForceDark';
import SamsungForceDarkBanner from './components/general/SamsungForceDarkBanner';
import { signOutLocally } from './utils';
import api from './api';
import { formatDate } from './utils';

const libraries = ['places']; // initializing libraries outside of the App component to make sure it does not create a fresh ['places'] every time the component re-runs, causing LoadScript to think the “libraries” prop changed.

function App() {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { showSamsungBanner, dismiss } = useSamsungBrowserBanner(); // detect if "force dark mode" is actively changing colors

  const { state, dispatch: ctxDispatch } = useContext(Store); // useContext gives access to the application state and lets me rename dispatch to ctxDisptach, to distinguish it from the dispatch of the current component
  const { cart, userInfo } = state; // using destructuring assignment, to filter the cart and userInfo states out of the Store

  const languages = {
    en: { nativeName: 'english' },
    de: { nativeName: 'deutsch' },
  };

  const [holidayStatus, setHolidayStatus] = useState(null);

  useEffect(() => {
    api
      .get('/api/settings/holiday')
      .then(({ data }) => {
        setHolidayStatus(data); // save the current holiday status returned by the backend
      })
      .catch((err) => {
        console.error('Failed to fetch holiday status:', err); // the status stays null, which keeps the banner hidden
      });
  }, []); // fetches the current holiday status once when the component loads

  const navbarRef = useRef(null);

  useEffect(() => {
    const navbarElement = navbarRef.current;

    if (!navbarElement) return;

    const publishNavbarHeight = () => {
      document.documentElement.style.setProperty(
        '--navbar-height',
        `${navbarElement.offsetHeight}px`
      );
    };

    publishNavbarHeight();

    const observer = new ResizeObserver(publishNavbarHeight);
    observer.observe(navbarElement);

    return () => observer.disconnect();
  }, []); // publishes the navbar's real height as a CSS variable, so the open burger menu can start right below it and reach the bottom of the screen without hardcoded heights

  const signoutHandler = async () => {
    try {
      await signOutLocally(); // removes the user information, the addresses and the payment method from the browsers local storage and runs the custom-layout purge (utils.js; the localStorage is modified here and not in the Store's action, as the stored token is not part of the application state the Store manages)

      ctxDispatch({ type: 'USER_SIGNOUT' }); // dispatches an 'USER_SIGNOUT' action to the Store
    } finally {
      setExpanded(false);
      window.location.href = '/signin'; // the user will be redirected to the signin screen, after he loggs out
    }
  };

  const handleClick = () => {
    if (window.innerWidth < 992) {
      setExpanded(!expanded);
    } // Checks if the screen size is smaller than 992px, because the collapsing of the navbar should only be triggered, if the burger menu is being shown. Otherwise the collapsation results in a flickering of the Links of the Navbar, whenever the screen is equal or greater than 992 px
  };

  const handleNavbarCollapse = () => {
    if (window.innerWidth < 992) {
      setExpanded(false);
    } // Checks if the screen size is smaller than 992px, because the collapsing of the navbar should only be triggered, if the burger menu is being shown. Otherwise the collapsation results in a flickering of the Links of the Navbar, whenever the screen is equal or greater than 992 px
  };

  useEffect(() => {
    if (!expanded) return;

    const collapse = () => {
      if (window.innerWidth < 992) {
        setExpanded(false);
      } // same width check as handleNavbarCollapse: only the burger menu collapses
    };

    const collapseOnClickOutside = (e) => {
      if (e.target.closest?.('#basic-navbar-nav, .navbar-toggler')) return; // the open menu itself and the burger button handle their own clicks (their links collapse the navbar, and dropdowns inside the menu stay usable)

      collapse();
    };

    const collapseOnEscape = (e) => {
      if (e.key === 'Escape') {
        collapse();
      }
    };

    document.addEventListener('click', collapseOnClickOutside);
    document.addEventListener('keydown', collapseOnEscape);

    return () => {
      document.removeEventListener('click', collapseOnClickOutside);
      document.removeEventListener('keydown', collapseOnEscape);
    };
  }, [expanded]); // closes the open burger menu on a click anywhere outside the navbar or on Escape

  const selectedLanguage = i18n.resolvedLanguage;

  return (
    <LoadScript
      googleMapsApiKey={`${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&loading=async`}
      libraries={libraries}
      language={selectedLanguage}
    >
      <BrowserRouter>
        {/* a custom component that ensures that the user is scrolled to the top of the new screen when navigating to another screen: */}
        <ScrollToTop />

        {/* custom component that is shown if the users samsung internet browser forces dark mode and is actively changing colors: */}
        {showSamsungBanner && <SamsungForceDarkBanner onDismiss={dismiss} />}

        {/* using BrowserRouter from react-router-dom to keep the UI in sync with the URL */}
        <div className="d-flex flex-column site-container">
          <ToastContainer position="bottom-center" limit={3} />
          {/* component from react-toastify that provides a context for the toast notifications to be displayed. When creating a toast notification using the toast() function (from react-toastify), the notification will be added to the <ToastContainer> and displayed on the screen | sets the position of the toast (alert message in the browser) to bottom center and limits the amount of toasts being shon at a time to 1 */}
          <header>
            <Navbar expand="lg" expanded={expanded} ref={navbarRef}>
              {/* when the screen size is smaller than the 'lg' breakpoint, the navbar will be hidden and its contents will not be visible (burger menu will show up) */}
              <Navbar.Brand
                as={Link}
                to="/"
                onClick={handleNavbarCollapse}
                className="brand"
              >
                <svg
                  width="240"
                  viewBox="-4 0 373.9697 53.172514"
                  version="1.1"
                  id="svg27"
                >
                  <defs id="SvgjsDefs2343" />
                  <g
                    id="SvgjsG2344"
                    featurekey="HKaMnE-0"
                    transform="matrix(0.5577558,0,0,0.5577558,-5.3943895,-1.3943895)"
                    fill="#000000"
                  >
                    <g id="g21">
                      <path
                        fill="#000000"
                        d="M 24.666,2.5 H 5.334 C 3.769,2.5 2.5,3.769 2.5,5.334 v 19.331 c 0,1.565 1.269,2.834 2.834,2.834 h 19.331 c 1.565,0 2.834,-1.269 2.834,-2.834 V 5.334 C 27.5,3.769 26.231,2.5 24.666,2.5 Z"
                        id="path3"
                      />
                      <path
                        fill="#000000"
                        d="M 59.704,2.5 H 40.373 c -1.565,0 -2.834,1.269 -2.834,2.834 v 19.331 c 0,1.565 1.269,2.834 2.834,2.834 h 19.331 c 1.565,0 2.835,-1.269 2.835,-2.834 V 5.334 C 62.539,3.769 61.27,2.5 59.704,2.5 Z"
                        id="path5"
                      />
                      <path
                        fill="#000000"
                        d="m 94.742,2.5 h -19.33 c -1.566,0 -2.835,1.269 -2.835,2.834 v 19.331 c 0,1.565 1.269,2.834 2.835,2.834 h 19.33 c 1.566,0 2.836,-1.269 2.836,-2.834 V 5.334 C 97.578,3.769 96.309,2.5 94.742,2.5 Z"
                        id="path7"
                      />
                      <path
                        fill="#000000"
                        d="M 24.666,37.667 H 5.334 c -1.565,0 -2.834,1.269 -2.834,2.834 v 19.331 c 0,1.565 1.269,2.834 2.834,2.834 h 19.331 c 1.565,0 2.834,-1.269 2.834,-2.834 V 40.501 C 27.5,38.936 26.231,37.667 24.666,37.667 Z"
                        id="path9"
                      />
                      <path
                        fill="#000000"
                        d="M 59.704,37.667 H 40.373 c -1.565,0 -2.834,1.269 -2.834,2.834 v 19.331 c 0,1.565 1.269,2.834 2.834,2.834 h 19.331 c 1.565,0 2.835,-1.269 2.835,-2.834 V 40.501 c 0,-1.565 -1.269,-2.834 -2.835,-2.834 z"
                        id="path11"
                      />
                      <path
                        fill="#000000"
                        d="m 94.742,37.667 h -19.33 c -1.566,0 -2.835,1.269 -2.835,2.834 v 19.331 c 0,1.565 1.269,2.834 2.835,2.834 h 19.33 c 1.566,0 2.836,-1.269 2.836,-2.834 V 40.501 c 0,-1.565 -1.269,-2.834 -2.836,-2.834 z"
                        id="path13"
                      />
                      <path
                        fill="#000000"
                        d="M 24.666,72.833 H 5.334 c -1.565,0 -2.834,1.269 -2.834,2.835 v 19.33 c 0,1.566 1.269,2.834 2.834,2.834 h 19.331 c 1.565,0 2.834,-1.268 2.834,-2.834 V 75.668 C 27.5,74.102 26.231,72.833 24.666,72.833 Z"
                        id="path15"
                      />
                      <path
                        fill="#000000"
                        d="M 59.704,72.833 H 40.373 c -1.565,0 -2.834,1.269 -2.834,2.835 v 19.33 c 0,1.566 1.269,2.834 2.834,2.834 h 19.331 c 1.565,0 2.835,-1.268 2.835,-2.834 v -19.33 c 0,-1.566 -1.269,-2.835 -2.835,-2.835 z"
                        id="path17"
                      />
                      <path
                        fill="#000000"
                        d="m 94.742,72.833 h -19.33 c -1.566,0 -2.835,1.269 -2.835,2.835 v 19.33 c 0,1.566 1.269,2.834 2.835,2.834 h 19.33 c 1.566,0 2.836,-1.268 2.836,-2.834 v -19.33 c 0,-1.566 -1.269,-2.835 -2.836,-2.835 z"
                        id="path19"
                      />
                    </g>
                  </g>
                  <g
                    id="SvgjsG2345"
                    featurekey="J3GnXt-0"
                    transform="matrix(2.0851683,0,0,2.0851683,71.123348,-2.1756225)"
                    fill="#000000"
                  >
                    <path
                      d="m 5.86,10.3 c 1.5067,0 2.72,0.5 3.64,1.5 L 8.14,13.04 C 7.48666,12.33334 6.7,11.98 5.78,11.98 c -0.88,0 -1.5933,0.31334 -2.14,0.94 -0.5467,0.62666 -0.82,1.4333 -0.82,2.42 0,0.93334 0.28666,1.7 0.86,2.3 0.57334,0.6 1.3067,0.90666 2.2,0.92 0.94666,0 1.7133,-0.35334 2.3,-1.06 l 1.28,1.28 C 8.60666,19.75334 7.4066,20.24 5.86,20.24 4.4067,20.24 3.2234,19.78666 2.31,18.88 1.3966,17.97334 0.9267,16.78 0.9,15.3 0.91334,13.7933 1.38334,12.5866 2.31,11.68 3.23666,10.7734 4.42,10.3133 5.86,10.3 Z m 6.94,0.22 v 5.68 c 0,0.74666 0.19666,1.3267 0.59,1.74 0.39334,0.4133 0.91668,0.62 1.57,0.62 0.84,0 1.49,-0.27666 1.95,-0.83 0.46,-0.55334 0.69,-1.3033 0.69,-2.25 v -4.96 h 1.8 V 20 h -1.8 v -1.46 h -0.04 c -0.25334,0.53334 -0.66668,0.95 -1.24,1.25 -0.57332,0.3 -1.2,0.45 -1.88,0.45 -1.0533,0 -1.89,-0.32666 -2.51,-0.98 C 11.31,18.60666 11,17.7533 11,16.7 v -6.18 z m 12.52,-0.24 c 0.72,0 1.3633,0.13338 1.93,0.40004 0.5667,0.26666 1.0033,0.65332 1.31,1.16 l -1.4,1.06 c -0.53334,-0.62666 -1.18,-0.94 -1.94,-0.94 -0.45334,0 -0.83,0.1 -1.13,0.3 -0.3,0.2 -0.45,0.45334 -0.45,0.76 0,0.66666 0.62,1.12 1.86,1.36 1.2133,0.22666 2.07,0.56 2.57,1 0.5,0.44 0.75,1.0667 0.75,1.88 0,0.92 -0.35,1.6433 -1.05,2.17 -0.7,0.5267 -1.6367,0.79666 -2.81,0.81 -0.88,0 -1.6033,-0.13666 -2.17,-0.41 -0.5667,-0.27334 -1.0567,-0.69668 -1.47,-1.27 l 1.36,-1.12 c 0.72,0.74666 1.4867,1.12 2.3,1.12 0.53334,0 0.98668,-0.10666 1.36,-0.32 0.37332,-0.21334 0.56,-0.50668 0.56,-0.88002 0,-0.65334 -0.6,-1.1067 -1.8,-1.36 -1.3333,-0.26666 -2.23,-0.62332 -2.69,-1.07 -0.46,-0.44668 -0.69,-1.0233 -0.69,-1.73 0,-0.88 0.34,-1.5833 1.02,-2.11 0.68,-0.5267 1.54,-0.79666 2.58,-0.81 z m 8.18,-2.42 -2e-5,2.66 h 2.58 v 1.56 h -2.58 v 4.56 c 0,0.61334 0.08666,1.0867 0.26,1.42 0.17334,0.3333 0.54,0.5 1.1,0.5 0.53334,0 0.96,-0.09334 1.28,-0.28 v 1.64 c -0.34666,0.18666 -0.89332,0.29332 -1.64,0.31998 -0.72,0 -1.2767,-0.11 -1.67,-0.33 -0.3933,-0.22 -0.68,-0.52666 -0.86,-0.92 -0.18,-0.39334 -0.27,-1.01 -0.27,-1.85 v -5.06 h -2.08 v -1.56 h 2.08 v -2.66 h 1.8 z m 9.48,2.42 c 1.4,0 2.5866,0.48338 3.56,1.45 0.9734,0.96662 1.4667,2.1434 1.48,3.53 0,1.4 -0.48666,2.5734 -1.46,3.52 -0.97334,0.9466 -2.1666,1.4333 -3.58,1.46 -1.3867,0 -2.5666,-0.48 -3.54,-1.44 -0.9734,-0.96 -1.4733,-2.14 -1.5,-3.54 0,-1.3733 0.48334,-2.54 1.45,-3.5 0.96666,-0.96 2.1634,-1.4533 3.59,-1.48 z m -3.12,4.98 c 0,0.97334 0.28664,1.7633 0.85998,2.37 0.57334,0.6067 1.3267,0.91666 2.26,0.93 0.94666,0 1.7,-0.30334 2.26,-0.91 0.56,-0.60666 0.84666,-1.4033 0.86,-2.39 0,-0.97334 -0.28,-1.7633 -0.84,-2.37 -0.56,-0.6067 -1.3267,-0.91666 -2.3,-0.93 -0.94666,0 -1.7,0.31334 -2.26,0.94 -0.56,0.62666 -0.84,1.4133 -0.84,2.36 z m 15.18,-4.98 c 1.3733,0 2.36,0.58666 2.96,1.76 0.30666,-0.56 0.72666,-0.99334 1.26,-1.3 0.53334,-0.30666 1.1133,-0.46 1.74,-0.46 2.3866,0 3.5866,1.3733 3.6,4.12 V 20 h -1.8 v -5.38 c 0,-0.93334 -0.16666,-1.61 -0.5,-2.03 -0.33334,-0.42 -0.84668,-0.63 -1.54,-0.63 -0.81334,0 -1.42,0.24666 -1.82,0.74 -0.4,0.49334 -0.6,1.1733 -0.6,2.04 V 20 h -1.8 v -5.1 c 0,-1.12 -0.16,-1.89 -0.48,-2.31 -0.32,-0.42 -0.82666,-0.63 -1.52,-0.63 -0.74666,0 -1.3433,0.28666 -1.79,0.86 -0.4467,0.57334 -0.67,1.3 -0.67,2.18 v 5 h -1.8 v -9.48 h 1.68 V 12 H 52 c 0.2,-0.46666 0.59666,-0.87 1.19,-1.21 0.59334,-0.34 1.21,-0.51 1.85,-0.51 z m 19.64,-5.4 v 9.76 l 4.1,-4.12 h 2.5 l -4.44,4.32 4.8,5.16 h -2.56 l -4.4,-4.92 V 20 h -1.8 V 4.88 Z m 12.92,5.4 c 1.4533,0 2.5834,0.45664 3.39,1.37 0.8066,0.91336 1.2167,2.17 1.23,3.77 v 0.5 h -7.6 c 0,0.77334 0.31,1.4233 0.93,1.95 0.62,0.5267 1.3767,0.79666 2.27,0.81 0.98666,0 1.8533,-0.47334 2.6,-1.42 l 1.36,1.04 c -1.0133,1.2933 -2.4134,1.94 -4.2,1.94 -1.4533,0 -2.6234,-0.46334 -3.51,-1.39 -0.8866,-0.92666 -1.3433,-2.1234 -1.37,-3.59 0,-1.4133 0.45666,-2.59 1.37,-3.53 0.91334,-0.94 2.09,-1.4233 3.53,-1.45 z m 2.7,4.2 c -0.02666,-0.89334 -0.28,-1.5767 -0.76,-2.05 -0.48,-0.4733 -1.14,-0.71 -1.98,-0.71 -0.56,0 -1.0633,0.13666 -1.51,0.41 -0.4467,0.27334 -0.79666,0.62668 -1.05,1.06 -0.25334,0.43332 -0.38,0.86334 -0.38,1.29 z m 5.02,-3.96 2.82,7.4 h 0.04 l 2.68,-7.4 h 1.94 L 98.32,22 c -0.41334,1.1067 -0.86668,1.85 -1.36,2.23 -0.49332,0.38 -1.1267,0.57 -1.9,0.57 -0.65334,0 -1.1867,-0.08666 -1.6,-0.26 l 0.22,-1.62 c 0.37334,0.13334 0.73334,0.2 1.08,0.2 0.56,0 0.96334,-0.13666 1.21,-0.41 0.24666,-0.27334 0.46332,-0.66334 0.64998,-1.17 l 0.56,-1.48 -3.94,-9.54 h 2.08 z m 13.56,-0.22 c 1.5067,0 2.72,0.5 3.64,1.5 l -1.36,1.24 c -0.65334,-0.70666 -1.44,-1.06 -2.36,-1.06 -0.88,0 -1.5933,0.31334 -2.14,0.94 -0.5467,0.62666 -0.82,1.4333 -0.82,2.42 0,0.93334 0.28666,1.7 0.86,2.3 0.57334,0.6 1.3067,0.90666 2.2,0.92 0.94666,0 1.7133,-0.35334 2.3,-1.06 l 1.28,1.28 c -0.85334,0.97334 -2.0534,1.46 -3.6,1.46 -1.4533,0 -2.6366,-0.45334 -3.55,-1.36 -0.9134,-0.90666 -1.3833,-2.1 -1.41,-3.58 0.0133,-1.5067 0.48334,-2.7134 1.41,-3.62 0.92666,-0.9066 2.11,-1.3667 3.55,-1.38 z m 8.94,-0.02 c 1.2,0 2.15,0.27328 2.85,0.81994 0.7,0.54666 1.07,1.3067 1.11,2.28 v 5.08 c 0,0.48 0.0267,0.99334 0.08,1.54 h -1.6 c -0.04,-0.42666 -0.06,-0.90666 -0.06,-1.44 h -0.04 c -0.41334,0.61334 -0.89,1.0467 -1.43,1.3 -0.54,0.2533 -1.17,0.38 -1.89,0.38 -0.97334,0 -1.7633,-0.26 -2.37,-0.78 -0.6067,-0.52 -0.91,-1.2067 -0.91,-2.06 0,-1.08 0.45334,-1.8967 1.36,-2.45 0.90666,-0.5533 2.1866,-0.83 3.84,-0.83 h 1.34 v -0.34 c 0,-0.64 -0.21,-1.1433 -0.63,-1.51 -0.42,-0.3667 -0.97,-0.55 -1.65,-0.55 -0.50666,0 -0.95,0.07666 -1.33,0.23 -0.38,0.15334 -0.83,0.43668 -1.35,0.85002 l -1.08,-1.12 c 1.0267,-0.90666 2.28,-1.3733 3.76,-1.4 z m -2.46,6.98 c 0,1.0267 0.68002,1.54 2.04,1.54 0.81334,0 1.4633,-0.24334 1.95,-0.73 0.4867,-0.48666 0.73666,-1.19 0.75,-2.11 v -0.52 h -1.02 c -1.1733,0 -2.0866,0.15 -2.74,0.45 -0.6534,0.3 -0.98,0.75666 -0.98,1.37 z m 14.2,-6.98 c 1.4,0 2.5434,0.45998 3.43,1.38 0.8866,0.92002 1.3433,2.12 1.37,3.6 0,1.48 -0.45334,2.6766 -1.36,3.59 -0.90666,0.9134 -2.06,1.3767 -3.46,1.39 -0.69334,0 -1.34,-0.14666 -1.94,-0.44 -0.6,-0.29334 -1.0533,-0.68 -1.36,-1.16 h -0.04 v 7 h -1.8 V 10.52 h 1.8 v 1.36 h 0.04 c 0.36,-0.50666 0.83334,-0.9 1.42,-1.18 0.58666,-0.28 1.22,-0.42 1.9,-0.42 z m -3.36,4.98 c 0,0.98666 0.28998,1.7833 0.86998,2.39 0.58,0.6067 1.33,0.91 2.25,0.91 0.96,0 1.7167,-0.30334 2.27,-0.91 0.5533,-0.60666 0.83668,-1.4033 0.85002,-2.39 0,-0.98666 -0.28334,-1.78 -0.85,-2.38 -0.56666,-0.6 -1.33,-0.90666 -2.29,-0.92 -0.94666,0 -1.7,0.31 -2.26,0.93 -0.56,0.62 -0.84,1.41 -0.84,2.37 z m 13.62,-4.98 c 0.72,0 1.3633,0.13338 1.93,0.40004 0.5667,0.26666 1.0033,0.65332 1.31,1.16 l -1.4,1.06 c -0.53334,-0.62666 -1.18,-0.94 -1.94,-0.94 -0.45334,0 -0.83,0.1 -1.13,0.3 -0.3,0.2 -0.45,0.45334 -0.45,0.76 0,0.66666 0.62,1.12 1.86,1.36 1.2133,0.22666 2.07,0.56 2.57,1 0.5,0.44 0.75,1.0667 0.75,1.88 0,0.92 -0.35,1.6433 -1.05,2.17 -0.7,0.5267 -1.6367,0.79666 -2.81,0.81 -0.88,0 -1.6033,-0.13666 -2.17,-0.41 -0.5667,-0.27334 -1.0567,-0.69668 -1.47,-1.27 l 1.36,-1.12 c 0.72,0.74666 1.4867,1.12 2.3,1.12 0.53334,0 0.98668,-0.10666 1.36,-0.32 0.37332,-0.21334 0.56,-0.50668 0.56,-0.88002 0,-0.65334 -0.6,-1.1067 -1.8,-1.36 -1.3333,-0.26666 -2.23,-0.62332 -2.69,-1.07 -0.46,-0.44668 -0.69,-1.0233 -0.69,-1.73 0,-0.88 0.34,-1.5833 1.02,-2.11 0.68,-0.5267 1.54,-0.79666 2.58,-0.81 z"
                      id="path24"
                    />
                  </g>
                </svg>
              </Navbar.Brand>

              <div className="d-flex">
                {/* used to create a button that toggles the collapse state of the Navbar when clicked. The aria-controls prop specifies the ID of the element that controls the Navbar's collapse */}
                <Navbar.Collapse className="header-links" id="basic-navbar-nav">
                  {/* used to define the collapsible content of the Navbar. The id prop is used to identify the collapsible content element. This ID should match the aria-controls value of the Navbar.Toggle component */}
                  <Nav className="me-auto w-100 justify-content-end">
                    {/* "me-auto" applies the margin-left: auto, wich pushes the Nav component to the right side of its container | "text-nowrap" prevents the text from wrapping onto the next line | "justify-content-end" applies the justify-content: end, wich aligns the content of the Nav component to the right side of the container */}

                    <Link
                      onClick={handleNavbarCollapse}
                      className="nav-link"
                      to="/customizer"
                    >
                      <div
                        className="link-container"
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                        }}
                      >
                        {t('app-customizer')}
                        <Badge
                          bg="secondary"
                          className="badge-beta"
                          style={{
                            position: 'absolute',
                            top: '-10px',
                            right: '-15px',
                            fontSize: '0.8rem',
                            padding: '3px 6px',
                            borderRadius: '5px',
                            zIndex: 10,
                          }}
                        >
                          Beta
                        </Badge>
                      </div>
                    </Link>
                    <Link
                      onClick={handleNavbarCollapse}
                      className="nav-link"
                      to="/community-designs"
                    >
                      {t('app-community-designs')}
                    </Link>
                    <Link
                      onClick={handleNavbarCollapse}
                      className="nav-link"
                      to="/faq"
                    >
                      {t('app-faq')}
                    </Link>
                    <Link
                      onClick={handleNavbarCollapse}
                      className="nav-link"
                      to="/contact"
                    >
                      {t('app-contact')}
                    </Link>
                    <div className="language-selector">
                      {Object.keys(languages).map((language, index, array) => (
                        <Fragment key={language}>
                          <Button
                            className="language-button"
                            type="submit"
                            onClick={() => {
                              i18n.changeLanguage(language);
                              handleNavbarCollapse();
                            }}
                            disabled={i18n.resolvedLanguage === language}
                          >
                            {language}
                          </Button>
                          {index < array.length - 1 && (
                            <span style={{ padding: '0px 6px' }}>|</span>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  </Nav>
                </Navbar.Collapse>

                {userInfo ? (
                  // if the userInfo exists (the user is signed in), the following dropdown menu will be shown
                  <NavDropdown
                    className="profile-icon exclude-dropdown-arrow"
                    onClick={handleNavbarCollapse}
                    title={
                      <svg
                        width="20"
                        height="20"
                        fill="currentColor"
                        className="bi bi-person"
                        viewBox="0 0 16 16"
                      >
                        <path d="M 8,8 A 4,4 0 1 0 8,0 4,4 0 0 0 8,8 M 10.666667,4 A 2.6666669,2.6666669 0 1 1 5.3333333,4 2.6666669,2.6666669 0 0 1 10.666667,4 M 16,14.666667 C 16,16 14.666667,16 14.666667,16 H 1.3333333 C 1.3333333,16 0,16 0,14.666667 0,13.333333 1.3333333,9.3333333 8,9.3333333 c 6.666667,0 8,3.9999997 8,5.3333337 m -1.333333,-0.0053 c -0.0013,-0.328 -0.205334,-1.314666 -1.109334,-2.218666 C 12.688,11.573333 11.052,10.666667 8,10.666667 c -3.0533333,0 -4.688,0.906666 -5.5573333,1.776 -0.904,0.904 -1.1066667,1.890666 -1.1093334,2.218666 z" />
                      </svg>
                    }
                    id="basic-nav-dropdown"
                  >
                    {/* a dropdown menu component from react-bootstrap */}
                    {userInfo && userInfo.isAdmin && (
                      <div>
                        <NavDropdown.Item
                          as={Link}
                          to="/admin/admin-dashboard"
                          className="admin-dashboard-nav-dropdown"
                        >
                          <strong>{t('app-admin-dashboard')}</strong>
                        </NavDropdown.Item>
                        <NavDropdown.Divider className="d-none d-sm-block" />
                      </div>
                    )}
                    <NavDropdown.Item as={Link} to="/profile">
                      {t('app-profile')}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/my-designs">
                      {t('app-designs')}
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/orderhistory">
                      {t('app-order-history')}
                    </NavDropdown.Item>
                    <NavDropdown.Divider className="d-none d-sm-block" />
                    <Link
                      className="dropdown-item sign-out"
                      to="#signout"
                      onClick={signoutHandler}
                    >
                      {t('app-sign-out')}
                    </Link>
                  </NavDropdown>
                ) : (
                  // if the userInfo doesn't exists (the user isn't signed in), a link to the SigninScreen will be shown
                  <Link
                    onClick={handleNavbarCollapse}
                    className="profile-icon-signin"
                    to="/signin"
                  >
                    <svg
                      width="20"
                      height="20"
                      fill="currentColor"
                      className="bi bi-person"
                      viewBox="0 0 16 16"
                    >
                      <path d="M 8,8 A 4,4 0 1 0 8,0 4,4 0 0 0 8,8 M 10.666667,4 A 2.6666669,2.6666669 0 1 1 5.3333333,4 2.6666669,2.6666669 0 0 1 10.666667,4 M 16,14.666667 C 16,16 14.666667,16 14.666667,16 H 1.3333333 C 1.3333333,16 0,16 0,14.666667 0,13.333333 1.3333333,9.3333333 8,9.3333333 c 6.666667,0 8,3.9999997 8,5.3333337 m -1.333333,-0.0053 c -0.0013,-0.328 -0.205334,-1.314666 -1.109334,-2.218666 C 12.688,11.573333 11.052,10.666667 8,10.666667 c -3.0533333,0 -4.688,0.906666 -5.5573333,1.776 -0.904,0.904 -1.1066667,1.890666 -1.1093334,2.218666 z" />
                    </svg>
                  </Link>
                )}

                <Link
                  onClick={handleNavbarCollapse}
                  to="/cart"
                  className="nav-link cart-container"
                >
                  {/* a link to the cart page */}
                  <div className="cart-icon-wrapper">
                    <svg
                      width="22"
                      height="22"
                      fill="currentColor"
                      className="cart-svg"
                      viewBox="0 0 16 16"
                    >
                      <path d="M 0,1.0666027 A 0.53333824,0.53333824 0 0 1 0.53333824,0.5332645 H 2.133353 A 0.53333824,0.53333824 0 0 1 2.6506911,0.93753489 L 3.0826951,2.6666175 H 15.466809 a 0.53333824,0.53333824 0 0 1 0.517338,0.6624061 L 14.384132,9.7290825 A 0.53333824,0.53333824 0 0 1 13.866794,10.133353 H 4.266706 A 0.53333824,0.53333824 0 0 1 3.7493679,9.7290825 L 1.7173491,1.599941 H 0.53333824 A 0.53333824,0.53333824 0 0 1 0,1.0666027 Z M 3.3493642,3.733294 4.6827098,9.0666764 H 13.450791 L 14.784136,3.733294 Z m 1.9840182,8.533411 a 1.0666765,1.0666765 0 1 0 0,2.133353 1.0666765,1.0666765 0 0 0 0,-2.133353 z m -2.1333529,1.066677 a 2.133353,2.133353 0 1 1 4.2667059,0 2.133353,2.133353 0 0 1 -4.2667059,0 z m 9.6000885,-1.066677 a 1.0666765,1.0666765 0 1 0 0,2.133353 1.0666765,1.0666765 0 0 0 0,-2.133353 z m -2.133353,1.066677 a 2.133353,2.133353 0 1 1 4.266706,0 2.133353,2.133353 0 0 1 -4.266706,0 z" />
                    </svg>
                    {cart.cartItems.length > 0 && (
                      <Badge pill className="cart-badge">
                        {/* if the items in the cart are greater than 0, a badge (object from react-bootstrap) will be shown, displaying the amount of items */}
                        {cart.cartItems.reduce((a, c) => a + c.quantity, 0)}
                        {/* calculates the amount of products in the cart and their quantity */}
                      </Badge>
                    )}
                  </div>
                </Link>

                <div className="burger-menu-container">
                  <Navbar.Toggle
                    aria-controls="basic-navbar-nav"
                    onClick={handleClick}
                  >
                    <svg
                      width="21"
                      height="21"
                      fill="black"
                      className="bi bi-list"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M 0,13.818182 A 0.72727272,0.72727272 0 0 1 0.72727276,13.09091 H 15.272727 a 0.72727272,0.72727272 0 0 1 0,1.454545 H 0.72727276 A 0.72727272,0.72727272 0 0 1 0,13.818182 Z M 0,7.9999999 A 0.72727272,0.72727272 0 0 1 0.72727276,7.2727273 H 15.272727 a 0.72727287,0.72727287 0 0 1 0,1.4545457 H 0.72727276 A 0.72727272,0.72727272 0 0 1 0,7.9999999 Z M 0,2.1818182 A 0.72727272,0.72727272 0 0 1 0.72727276,1.4545454 H 15.272727 a 0.72727275,0.72727275 0 0 1 0,1.4545455 H 0.72727276 A 0.72727272,0.72727272 0 0 1 0,2.1818182 Z"
                      />
                    </svg>
                  </Navbar.Toggle>
                </div>
              </div>
            </Navbar>
          </header>

          {holidayStatus?.showBanner && (
            <div className="holiday-banner">
              <span>
                🏖️{' '}
                {t('app-holiday-banner', {
                  startDate: formatDate(holidayStatus.holidayStartDate, {
                    dateOnly: true,
                  }),
                  endDate: formatDate(holidayStatus.holidayEndDate, {
                    dateOnly: true,
                  }),
                  deferralDate: formatDate(
                    holidayStatus.orderDeferralStartDate,
                    { dateOnly: true }
                  ),
                  resumeDate: formatDate(holidayStatus.processingResumeDate, {
                    dateOnly: true,
                  }),
                })}
              </span>
            </div>
          )}

          <main>
            {/* the className="mt-3" will add a margin-top of 3rem */}
            <Routes>
              {/* using Routes from react-router-dom to enable routing for different screens */}
              <Route path="/" element={<HomeScreen />} />
              <Route
                path="/community-designs"
                element={<CommunityDesignsScreen />}
              />
              <Route
                path="/customizer/:designId?"
                element={<CustomizerScreen />}
              />
              <Route path="/cart" element={<CartScreen />} />
              <Route path="/signin" element={<SigninScreen />} />
              <Route path="/signup" element={<SignupScreen />} />
              <Route
                path="/forgot-password"
                element={<ForgotPasswordScreen />}
              />
              <Route
                path="/reset-password/:token"
                element={<ResetPasswordScreen />}
              />
              <Route path="/imprint" element={<ImprintScreen />} />
              <Route
                path="/credits-and-licenses"
                element={<CreditsAndLicensesScreen />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
              <Route
                path="/general-terms-and-conditions"
                element={<GeneralTermsAndConditionsScreen />}
              />
              <Route
                path="/right-of-withdrawal"
                element={<RightOfWithdrawalScreen />}
              />
              <Route path="/faq" element={<FAQScreen />} />
              <Route path="/contact" element={<ContactScreen />} />
              <Route
                path="/verify-email"
                element={<EmailVerificationScreen />}
              />
              <Route
                path="/please-verify-your-email"
                element={<EmailVerificationPromtScreen />}
              />
              <Route path="/shipping-costs" element={<ShippingCostsScreen />} />

              {/* Protected Routes: */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    {/* ProtectedRoute is a custom wrapper for other Route components. Before routing to the ProfileScreen in this case, it checks if the user is signed in and only than allows the user to access this route */}
                    <ProfileScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shipping-address"
                element={
                  <ProtectedRoute>
                    <ShippingAddressScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment"
                element={
                  <ProtectedRoute>
                    <PaymentMethodScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/placeorder"
                element={
                  <ProtectedRoute>
                    <PlaceOrderScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order/:orderId"
                element={
                  <ProtectedRoute>
                    <OrderScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orderhistory"
                element={
                  <ProtectedRoute>
                    <OrderHistoryScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-designs"
                element={
                  <ProtectedRoute>
                    <MyDesignsScreen />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes: */}
              <Route
                path="/admin/admin-dashboard"
                element={
                  <AdminRoute>
                    {/* AdminRoute is a custom wrapper for other Route components. Before routing to the AdminDashboardScreen in this case, it checks if the user is an admin and only than allows the user to access this route */}
                    <AdminDashboardScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/layouts"
                element={
                  <AdminRoute>
                    {/* AdminRoute is a custom wrapper for other Route components. Before routing to the AdminDashboardScreen in this case, it checks if the user is an admin and only than allows the user to access this route */}
                    <LayoutListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/stock"
                element={
                  <AdminRoute>
                    <StockScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/products"
                element={
                  <AdminRoute>
                    <ProductListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/designs"
                element={
                  <AdminRoute>
                    <DesignListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/orders"
                element={
                  <AdminRoute>
                    <OrderListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <UserListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/legends"
                element={
                  <AdminRoute>
                    <LegendsListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/molds"
                element={
                  <AdminRoute>
                    <MoldsListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/zones"
                element={
                  <AdminRoute>
                    <ZoneListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/fonts"
                element={
                  <AdminRoute>
                    <FontListScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/colors"
                element={
                  <AdminRoute>
                    <ColorsScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/process-order/:orderId"
                element={
                  <AdminRoute>
                    <ProcessOrderScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/keycap/:id"
                element={
                  <AdminRoute>
                    <KeycapEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/cardboard-box/:id"
                element={
                  <AdminRoute>
                    <CardboardBoxEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/foam/:id"
                element={
                  <AdminRoute>
                    <FoamEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/transfer-film/:id"
                element={
                  <AdminRoute>
                    <TransferFilmEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/ink/:id"
                element={
                  <AdminRoute>
                    <InkEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/keycap-puller/:id"
                element={
                  <AdminRoute>
                    <KeycapPullerEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/shipping-box/:id"
                element={
                  <AdminRoute>
                    <ShippingBoxEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/product/:id"
                element={
                  <AdminRoute>
                    <ProductEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/user/:id"
                element={
                  <AdminRoute>
                    <UserEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/layout/:id"
                element={
                  <AdminRoute>
                    <LayoutEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/mold/:id"
                element={
                  <AdminRoute>
                    <MoldEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/zone/:id"
                element={
                  <AdminRoute>
                    <ZoneEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/legend-set/:id"
                element={
                  <AdminRoute>
                    <LegendsEditScreen />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/font/:id"
                element={
                  <AdminRoute>
                    <FontEditScreen />
                  </AdminRoute>
                }
              />
            </Routes>
          </main>
          <footer>
            <Container fluid="xl" className="mt-5">
              <Row className="footer-main">
                <Col className="footer-container" md={5} sm={12}>
                  <Row>
                    <h3 className="footer-head">
                      <strong>{t('app-legal')}</strong>
                    </h3>
                  </Row>
                  <Link className="nav-link" to="/general-terms-and-conditions">
                    {t('app-general-terms-and-conditions')}
                  </Link>
                  <Link className="nav-link" to="/right-of-withdrawal">
                    {t('app-right-of-withdrawal')}
                  </Link>
                  <Link className="nav-link" to="/privacy-policy">
                    {t('app-data-protection')}
                  </Link>
                  <Link className="nav-link" to="/imprint">
                    {t('app-imprint')}
                  </Link>
                  <Link className="nav-link" to="/credits-and-licenses">
                    {t('app-credits-and-licenses')}
                  </Link>
                </Col>
                <Col className="footer-container" md={2} sm={12}>
                  <Row>
                    <Link className="nav-link-header" to="/faq">
                      <h3 className="footer-head">
                        <strong>{t('app-faq-footer')}</strong>
                      </h3>
                    </Link>
                  </Row>
                  <Link className="nav-link" to="/faq#faq-1">
                    {t('app-placeholder-faq-1')}
                  </Link>
                  <Link className="nav-link" to="/faq#faq-2">
                    {t('app-placeholder-faq-2')}
                  </Link>
                  <Link className="nav-link" to="/faq#faq-5">
                    {t('app-placeholder-faq-3')}
                  </Link>
                </Col>
                <Col className="footer-container" md={5} sm={12}>
                  <Row>
                    <Link className="nav-link-header" to="/contact">
                      <h3 className="footer-head">
                        <strong>{t('app-contact-footer')}</strong>
                      </h3>
                    </Link>
                  </Row>
                  <div className="contact-info">
                    <div className="d-flex align-items-center justify-content-center">
                      <a
                        href="https://www.reddit.com/r/CustomKeycaps_/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg
                          viewBox="0 0 26 26"
                          width="26"
                          height="26"
                          fill="currentColor"
                        >
                          <path d="m 16.9377,16.57175 c -0.9906,0 -1.8213,-0.81354 -1.8213,-1.81688 0,-1.00464 0.8307,-1.84457 1.8213,-1.84457 0.9893,0 1.7914,0.83993 1.7914,1.84457 0,1.00334 -0.8008,1.81688 -1.7914,1.81688 m 0.2522,3.35673 c -0.8749,0.88608 -2.2464,1.31716 -4.1912,1.31716 -1.9461,0 -3.3163,-0.43108 -4.1899,-1.31716 -0.1898,-0.19253 -0.1898,-0.50362 0,-0.69745 0.1885,-0.19253 0.4966,-0.19253 0.6864,0 0.6825,0.69225 1.8278,1.02973 3.5035,1.02973 1.6744,0 2.821,-0.33748 3.5048,-1.02973 0.1885,-0.19253 0.4966,-0.19253 0.6864,0 0.1898,0.19383 0.1898,0.50492 0,0.69745 M 7.2696,14.75487 c 0,-1.00334 0.8294,-1.84457 1.8187,-1.84457 0.9893,0 1.7914,0.84123 1.7914,1.84457 0,1.00334 -0.8021,1.81688 -1.7914,1.81688 -0.9893,0 -1.8187,-0.81354 -1.8187,-1.81688 M 26,12.75742 c 0,-1.64684 -1.3208,-2.98636 -2.9445,-2.98636 -0.7488,0 -1.4547,0.28353 -1.9955,0.79105 C 19.1165,9.27927 16.5139,8.45 13.6318,8.33664 l 1.5899,-4.9218 4.1756,0.99671 c 0.0013,1.35538 1.0881,2.45635 2.4245,2.45635 1.3364,0 2.4245,-1.10357 2.4245,-2.45895 C 24.2463,3.05357 23.1582,1.95 21.8218,1.95 20.8208,1.95 19.9615,2.56698 19.591,3.44643 18.6914,3.23154 15.4752,2.46285 14.5756,2.24796 l -1.963,6.07815 C 9.6213,8.38331 6.9108,9.20816 4.8945,10.52129 4.3602,10.04003 3.6686,9.77106 2.9432,9.77106 1.3208,9.77106 0,11.11058 0,12.75742 0,13.80288 0.5382,14.7615 1.3988,15.29944 0.6149,19.99569 6.071,24.05 12.9454,24.05 c 6.8458,0 12.2837,-4.01869 11.5583,-8.69128 C 25.4241,14.83534 26,13.85436 26,12.75742" />
                        </svg>
                      </a>
                      <a
                        href="https://www.tiktok.com/@CustomKeycaps_"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="26"
                          height="26"
                          fill="currentColor"
                          className="bi bi-tiktok"
                          viewBox="0 0 16 16"
                        >
                          <path d="M9 0h1.98c.144.715.54 1.617 1.235 2.512C12.895 3.389 13.797 4 15 4v2c-1.753 0-3.07-.814-4-1.829V11a5 5 0 1 1-5-5v2a3 3 0 1 0 3 3z" />
                        </svg>
                      </a>
                      <a
                        href="https://www.instagram.com/CustomKeycaps_/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="26"
                          height="26"
                          fill="currentColor"
                          className="bi bi-instagram"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.9 3.9 0 0 0-1.417.923A3.9 3.9 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.9 3.9 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.9 3.9 0 0 0-.923-1.417A3.9 3.9 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599s.453.546.598.92c.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.5 2.5 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.5 2.5 0 0 1-.92-.598 2.5 2.5 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233s.008-2.388.046-3.231c.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92s.546-.453.92-.598c.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92m-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217m0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334" />
                        </svg>
                      </a>
                      <a
                        href="https://www.youtube.com/@CustomKeycaps"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="26"
                          height="26"
                          fill="currentColor"
                          className="bi bi-youtube"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.01 2.01 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.01 2.01 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31 31 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.01 2.01 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A100 100 0 0 1 7.858 2zM6.4 5.209v4.818l4.157-2.408z" />
                        </svg>
                      </a>
                    </div>
                    <p>{t('app-contact-footer-info-1')}</p>
                    <p>{t('app-contact-footer-info-2')}</p>
                    <p>{t('app-contact-footer-info-3')}</p>
                  </div>
                </Col>
              </Row>
              <Row className="footer-secondary my-2">
                <Col className="d-none d-md-block" sm={12} md={12} lg={12}>
                  {/* d-none: disbley none | d-md-block: display block if screen size ≥ md (768px) */}
                  <span className="copyright-footer">
                    {t('app-copy-right')}
                  </span>{' '}
                  |{' '}
                  <span className="hanseaticode-link">
                    {t('app-developed-by-1')}{' '}
                    <a
                      href="https://www.hanseaticode.de"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('app-developed-by-2')}
                    </a>
                  </span>
                </Col>
                <Col className="d-md-none" sm={12} md={12} lg={12}>
                  {/* d-md-none: disbley none if screen size ≥ md (768px) */}
                  <span className="copyright-footer">
                    {t('app-copy-right')}
                  </span>
                </Col>
                <Col className="d-md-none" sm={12} md={12} lg={12}>
                  {/* d-md-none: disbley none if screen size ≥ md (768px) */}
                  <span className="hanseaticode-link">
                    {t('app-developed-by-1')}{' '}
                    <a
                      href="https://www.hanseaticode.de"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('app-developed-by-2')}
                    </a>
                  </span>
                </Col>
              </Row>
            </Container>
          </footer>
        </div>
      </BrowserRouter>
    </LoadScript>
  );
}

export default App;
